import { useQuery, keepPreviousData } from '@tanstack/react-query';
import type { DataSourceLoadOptions, OrderToken } from '@sleed/bizcommerce-api';
import { ordersApi } from '~/lib/api/client';
import { applySort, type SortDir } from '~/lib/grid/sort';

/** Sales-person / B2B order filter (legacy `bizUserType=3` on `/orders/getall`). */
const BIZ_USER_TYPE_SALESPERSON = 3;

export interface FilteredOrdersParams {
	skip?: number;
	take?: number;
	/** Free-text search (→ searchText: code / customer / email / phone). */
	search?: string;
	/** Purchase-date sort direction (default desc). */
	sortDir?: SortDir;
}

export interface FilteredOrdersResult {
	items: OrderToken[];
	totalCount: number;
}

export const filteredOrderKeys = {
	all: ['filtered-orders'] as const,
	list: (params: unknown) => [...filteredOrderKeys.all, 'list', params] as const
};

/**
 * Server-paged sales-person orders via `ordersApi.getAll` with `bizUserType=3` (mirrors the legacy
 * admin's /sp-orders grid). Response is `OrderTokenListGrid` (reads `res.data.data` + `totalCount`).
 */
export function useFilteredOrdersList(params: FilteredOrdersParams = {}) {
	const { skip = 0, take = 20, search = '', sortDir = 'desc' } = params;
	return useQuery<FilteredOrdersResult>({
		queryKey: filteredOrderKeys.list({ skip, take, search, sortDir }),
		placeholderData: keepPreviousData,
		queryFn: async () => {
			const loadOptions: DataSourceLoadOptions = { skip, take, requireTotalCount: true };
			const extra = loadOptions as Record<string, unknown>;
			extra.bizUserType = BIZ_USER_TYPE_SALESPERSON;
			if (search.trim()) extra.searchText = search.trim();
			applySort(loadOptions, 'pdt', sortDir);
			const res = await ordersApi.getAll(loadOptions);
			return { items: res.data.data ?? [], totalCount: res.data.totalCount ?? 0 };
		}
	});
}
